import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, formatINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Check, Package, RotateCcw, XCircle, CheckCircle2, Clock, LifeBuoy } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

export default function Account() {
  const {
    user,
    logout,
    loading,
    requestWhatsAppOtp,
    verifyWhatsAppOtp,
  } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [returns, setReturns] = useState([]);
  const [showReturn, setShowReturn] = useState(null);   // order object
  const [returnForm, setReturnForm] = useState({ type: "return", reason: "", reason_details: "" });
  const [whatsappPhone, setWhatsappPhone] = useState("");
  const [whatsappOtp, setWhatsappOtp] = useState("");
  const [whatsappStep, setWhatsappStep] = useState("phone");
  const [whatsappLoading, setWhatsappLoading] = useState(false);
  const tab = params.get("tab") || "profile";

  useEffect(() => {
    if (loading) return;
    if (!user) {
      navigate("/login");
      return;
    }
    api.get("/orders").then((r) => setOrders(r.data));
    api.get("/returns/me").then((r) => setReturns(r.data)).catch(() => {});
  }, [user, loading, navigate]);

  const sendWhatsAppOtp = async () => {
    setWhatsappLoading(true);
    try {
      await requestWhatsAppOtp(whatsappPhone);
      setWhatsappStep("otp");
      toast.success("Verification code sent through WhatsApp");
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Unable to send the WhatsApp verification code"
      );
    } finally {
      setWhatsappLoading(false);
    }
  };

  const confirmWhatsAppOtp = async () => {
    if (!/^\d{6}$/.test(whatsappOtp)) {
      toast.error("Enter the 6-digit verification code");
      return;
    }

    setWhatsappLoading(true);
    try {
      await verifyWhatsAppOtp(whatsappPhone, whatsappOtp);
      setWhatsappOtp("");
      setWhatsappStep("phone");
      toast.success("WhatsApp number verified");
    } catch (e) {
      toast.error(
        e.response?.data?.detail ||
          "Unable to verify the WhatsApp code"
      );
    } finally {
      setWhatsappLoading(false);
    }
  };

  const handleLogout = () => {
    navigate("/login", { replace: true, state: { loggedOut: true } });
    window.setTimeout(() => logout(), 0);
  };

  const submitReturn = async () => {
    if (!returnForm.reason) { toast.error("Please pick a reason"); return; }
    const items = (showReturn.items || []).map((it) => ({
      product_id: it.product_id || it.id,
      name: it.product_name || it.name || "Item",
      quantity: it.quantity,
      price: it.price,
    }));
    try {
      await api.post(`/orders/${showReturn.id}/return`, { order_id: showReturn.id, items, ...returnForm });
      toast.success("Return request submitted — we'll email you soon");
      setShowReturn(null);
      setReturnForm({ type: "return", reason: "", reason_details: "" });
      const r = await api.get("/returns/me");
      setReturns(r.data);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Failed to submit return");
    }
  };

  if (!user) return null;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-8 pt-[132px] pb-10 md:pt-[140px] md:pb-12">
      <h1 className="font-serif text-4xl sm:text-5xl text-[#1C1917] mb-6">
        Namaste, {user.name.split(" ")[0]}
      </h1>

      <Tabs
        value={tab}
        onValueChange={(value) =>
          navigate(`/account?tab=${encodeURIComponent(value)}`)
        }
        className="w-full"
      >
        <TabsList className="bg-[#F5F5F4] border border-[#E7E5E4]">
          <TabsTrigger value="profile" data-testid="tab-profile">Profile</TabsTrigger>
          <TabsTrigger value="orders" data-testid="tab-orders">Orders</TabsTrigger>
          <TabsTrigger value="returns" data-testid="tab-returns">Returns</TabsTrigger>
          <TabsTrigger
            value="help-support"
            data-testid="tab-help-support"
            onClick={() => navigate("/help-support")}
          >
            Help & Support
          </TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-8">
          <div className="bg-white border border-[#E7E5E4] rounded-lg p-8 max-w-lg">
            <h2 className="font-serif text-2xl mb-6">Profile</h2>
            <div className="space-y-4 text-sm">
              <div>
                <div className="text-[#78716C] mb-1">Name</div>
                <div className="font-medium">{user.name}</div>
              </div>
              <div>
                <div className="text-[#78716C] mb-1">Email</div>
                <div className="font-medium">{user.email}</div>
              </div>
            </div>

            <div className="mt-6 pt-6 border-t border-[#E7E5E4]">
              <div className="text-[#78716C] text-sm mb-1">WhatsApp</div>

              {user.whatsapp_verified ? (
                <div>
                  <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                    <CheckCircle2 className="h-4 w-4" />
                    Verified
                  </div>
                  <div className="text-sm mt-1">
                    {user.whatsapp_phone}
                  </div>
                  <p className="text-xs text-[#78716C] mt-2">
                    Enabled for customer sign-in and transactional order updates.
                  </p>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-xs text-[#78716C]">
                    Link your WhatsApp number for secure sign-in and order updates.
                  </p>

                  <input
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="10-digit mobile number"
                    value={whatsappPhone}
                    onChange={(e) => setWhatsappPhone(e.target.value)}
                    disabled={whatsappLoading || whatsappStep === "otp"}
                    className="flex h-10 w-full rounded-md border border-[#D6D3D1] bg-white px-3 py-2 text-sm"
                    data-testid="profile-whatsapp-phone"
                  />

                  {whatsappStep === "otp" ? (
                    <>
                      <input
                        type="text"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        placeholder="6-digit verification code"
                        value={whatsappOtp}
                        onChange={(e) =>
                          setWhatsappOtp(
                            e.target.value.replace(/\D/g, "").slice(0, 6)
                          )
                        }
                        disabled={whatsappLoading}
                        className="flex h-10 w-full rounded-md border border-[#D6D3D1] bg-white px-3 py-2 text-sm"
                        data-testid="profile-whatsapp-otp"
                      />
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          onClick={confirmWhatsAppOtp}
                          disabled={whatsappLoading}
                          className="rounded-full bg-[#128C7E] hover:bg-[#0F766E] text-white"
                          data-testid="profile-whatsapp-verify"
                        >
                          {whatsappLoading ? "Verifying..." : "Verify WhatsApp"}
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          disabled={whatsappLoading}
                          onClick={() => {
                            setWhatsappStep("phone");
                            setWhatsappOtp("");
                          }}
                          className="rounded-full"
                        >
                          Change number
                        </Button>
                      </div>
                    </>
                  ) : (
                    <Button
                      type="button"
                      onClick={sendWhatsAppOtp}
                      disabled={whatsappLoading || !whatsappPhone.trim()}
                      className="rounded-full bg-[#128C7E] hover:bg-[#0F766E] text-white"
                      data-testid="profile-whatsapp-send"
                    >
                      {whatsappLoading
                        ? "Sending..."
                        : "Send verification code"}
                    </Button>
                  )}
                </div>
              )}
            </div>

            <Button
              onClick={handleLogout}
              variant="outline"
              className="mt-8 rounded-full"
              data-testid="profile-logout-btn"
            >
              Sign out
            </Button>
          </div>
        </TabsContent>

        <TabsContent value="orders" className="mt-5">
          {orders.length === 0 ? (
            <div className="mx-auto max-w-md rounded-2xl border border-[#E8DCCB] bg-white p-7 text-center shadow-sm">
              <Package className="mx-auto h-8 w-8 text-[#7C1F30]" />
              <h2 className="mt-3 font-serif text-2xl text-[#2A1508]">No orders yet</h2>
              <p className="mt-2 text-sm text-[#6F6157]">When you place an order, you can track it here.</p>
              <Button type="button" onClick={() => navigate("/shop")}
                className="mt-5 rounded-full bg-[#7C1F30] text-white hover:bg-[#8D2A3D]">
                Explore the shop
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {orders.map((o) => (
                <div
                  key={o.id}
                  data-testid={`order-${o.id}`}
                  className="bg-white border border-[#E7E5E4] rounded-lg p-6"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3 mb-6 pb-4 border-b border-[#E7E5E4]">
                    <div>
                      <div className="flex items-center gap-2 text-sm">
                        <Package className="h-4 w-4 text-[#7C1F30]" />
                        <span className="font-medium">Order #{o.id.slice(0, 8)}</span>
                      </div>
                      <div className="text-xs text-[#78716C] mt-1">
                        Placed on {new Date(o.created_at).toLocaleDateString()}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-medium">{formatINR(o.total_inr)}</div>
                      <div
                        className={`text-xs mt-1 px-2 py-0.5 rounded-full inline-block ${
                          o.payment_status === "paid"
                            ? "bg-green-100 text-green-800"
                            : "bg-yellow-100 text-yellow-800"
                        }`}
                      >
                        {o.payment_status}
                      </div>
                      {(
                        String(o.payment_method || "").toLowerCase() === "cod"
                          ? (
                              o.status === "delivered" &&
                              o.courier_delivery_source === "shiprocket" &&
                              Boolean(o.courier_delivery_confirmed_at) &&
                              o.payment_status !== "refunded"
                            )
                          : o.payment_status === "paid"
                      ) && (
                        <div className="mt-2">
                          <a
                            href={`/invoice/${o.id}`}

                            rel="noreferrer"
                            data-testid={`invoice-link-${o.id}`}
                            className="text-xs text-[#7C1F30] underline hover:text-[#8D2A3D]"
                          >
                            View Invoice ›
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tracking */}
                  <div className="mb-6">
                    <div className="flex items-center justify-between">
                      {o.tracking_steps.map((s, i) => (
                        <div key={i} className="flex-1 flex items-center">
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${
                              s.completed
                                ? "bg-[#7C1F30] text-white"
                                : "bg-[#F5F5F4] text-[#78716C] border border-[#E7E5E4]"
                            }`}
                          >
                            {s.completed ? <Check className="h-4 w-4" /> : i + 1}
                          </div>
                          {i < o.tracking_steps.length - 1 && (
                            <div
                              className={`flex-1 h-0.5 mx-2 ${
                                s.completed ? "bg-[#7C1F30]" : "bg-[#E7E5E4]"
                              }`}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex justify-between mt-2 text-[10px] text-[#78716C]">
                      {o.tracking_steps.map((s, i) => (
                        <div key={i} className="flex-1 text-center">
                          {s.label}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="space-y-2">
                    {o.items.map((it, idx) => (
                      <div key={idx} className="flex gap-3 items-center text-sm">
                        <img src={it.image} alt="" className="w-12 h-16 object-cover rounded" />
                        <div className="flex-1">
                          <div className="line-clamp-1">{it.product_name}</div>
                          <div className="text-xs text-[#78716C]">
                            Qty {it.quantity} · {formatINR(it.subtotal)}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {o.courier_name && (
                    <div className="mt-4 pt-4 border-t border-[#E7E5E4] text-sm" data-testid={`tracking-info-${o.id}`}>
                      <div className="text-xs uppercase tracking-wider text-[#78716C] mb-1">Courier tracking</div>
                      <div className="font-medium">{o.courier_name}</div>
                      <div className="text-xs text-[#78716C]">AWB: <span className="font-mono">{o.tracking_id}</span></div>
                      {o.tracking_url && (
                        <a href={o.tracking_url} target="_blank" rel="noreferrer" className="text-xs text-[#7C1F30] underline hover:text-[#8D2A3D] inline-block mt-1">
                          Track shipment ›
                        </a>
                      )}
                    </div>
                  )}
                  {o.payment_method === "cod" && (
                    <div className="mt-3 text-xs bg-yellow-50 border border-yellow-200 text-yellow-800 px-3 py-2 rounded">
                      💵 Cash on Delivery — please have {formatINR(o.total_inr)} ready
                    </div>
                  )}
                  {o.payment_status === "refunded" && (
                    <div className="mt-3 text-xs bg-blue-50 border border-blue-200 text-blue-800 px-3 py-2 rounded" data-testid={`refunded-${o.id}`}>
                      ✓ Refund of {formatINR(o.refund_amount || o.total_inr)} initiated to your payment source
                    </div>
                  )}

                  {/* Return / Exchange button — visible for shipped or delivered orders only */}
                  {(o.status === "shipped" || o.status === "delivered") && !returns.some((r) => r.order_id === o.id && !["cancelled", "rejected"].includes(r.status)) && (
                    <div className="mt-3">
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs rounded-full"
                        onClick={() => { setShowReturn(o); setReturnForm({ type: "return", reason: "", reason_details: "" }); }}
                        data-testid={`return-btn-${o.id}`}
                      >
                        <RotateCcw className="h-3 w-3 mr-1" /> Request Return / Exchange
                      </Button>
                    </div>
                  )}
                  <div className="mt-4 border-t border-[#E7E5E4] pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="rounded-full border-[#176963]/40 text-xs text-[#176963] hover:bg-[#EAF5F2]"
                      onClick={() =>
                        navigate(`/help-support?orderId=${encodeURIComponent(o.id)}`)
                      }
                      data-testid={`order-help-support-${o.id}`}
                    >
                      <LifeBuoy className="mr-1.5 h-3.5 w-3.5" />
                      Get help with this order
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Returns Tab */}
        <TabsContent value="returns" className="mt-8">
          {returns.length === 0 ? (
            <div className="py-16 text-center text-[#78716C]" data-testid="no-returns">No return requests yet.</div>
          ) : (
            <div className="space-y-4" data-testid="returns-list">
              {returns.map((r) => {
                const status = r.status;
                const badge = status === "refunded" || status === "exchanged" ? "bg-green-100 text-green-800"
                  : status === "rejected" || status === "cancelled" ? "bg-red-100 text-red-800"
                  : status === "approved" || status === "received" || status === "picked_up" ? "bg-blue-100 text-blue-800"
                  : "bg-yellow-100 text-yellow-800";
                const Icon = status === "refunded" || status === "exchanged" ? CheckCircle2 : status === "rejected" ? XCircle : Clock;
                return (
                  <div key={r.id} className="bg-white border border-[#E7E5E4] rounded-lg p-6" data-testid={`return-row-${r.id}`}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <div className="text-xs uppercase tracking-wider text-[#78716C] mb-1">{r.type.replace("_", " ")}</div>
                        <div className="font-serif text-lg">{r.order_number}</div>
                        <div className="text-xs text-[#78716C]">{new Date(r.created_at).toLocaleString()}</div>
                      </div>
                      <span className={`text-xs px-3 py-1 rounded-full ${badge} flex items-center gap-1`}>
                        <Icon className="h-3 w-3" /> {status}
                      </span>
                    </div>
                    <div className="text-sm space-y-1">
                      <div><span className="text-[#78716C]">Items:</span> {r.items.map((i) => `${i.name} × ${i.quantity}`).join(", ")}</div>
                      <div><span className="text-[#78716C]">Reason:</span> {r.reason}</div>
                      {r.reason_details && <div className="text-xs text-[#78716C]">{r.reason_details}</div>}
                      <div><span className="text-[#78716C]">Refund amount:</span> {formatINR(r.refund_amount)}</div>
                      {r.refund_id && <div className="text-xs text-[#78716C]">Refund ID: <span className="font-mono">{r.refund_id}</span></div>}
                      {r.admin_notes && <div className="mt-2 text-xs bg-[#FAF3E7] p-2 rounded"><strong>Admin:</strong> {r.admin_notes}</div>}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Request Return Dialog */}
      <Dialog open={!!showReturn} onOpenChange={(v) => !v && setShowReturn(null)}>
        <DialogContent className="bg-white max-w-md" data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Request Return / Exchange</DialogTitle>
          </DialogHeader>
          {showReturn && (
            <div className="space-y-4 text-sm">
              <div className="bg-[#FAF3E7] p-3 rounded text-xs">
                <div><strong>Order:</strong> {showReturn.invoice_number}</div>
                <div><strong>Items:</strong> {(showReturn.items || []).map((i) => `${i.product_name || i.name} × ${i.quantity}`).join(", ")}</div>
                <div><strong>Refund amount:</strong> {formatINR(showReturn.total_inr)}</div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#78716C] block mb-1">Type</label>
                <div className="flex gap-2">
                  {[
                    { v: "return", label: "Return + Refund" },
                    { v: "exchange", label: "Exchange" },
                    { v: "refund_only", label: "Refund only (damaged)" },
                  ].map((t) => (
                    <button
                      key={t.v}
                      type="button"
                      onClick={() => setReturnForm({ ...returnForm, type: t.v })}
                      className={`px-3 py-1.5 text-xs rounded-full border ${returnForm.type === t.v ? "bg-[#7C1F30] text-white border-[#7C1F30]" : "border-[#E7E5E4] text-[#78716C]"}`}
                      data-testid={`return-type-${t.v}`}
                    >{t.label}</button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#78716C] block mb-1">Reason</label>
                <select
                  value={returnForm.reason}
                  onChange={(e) => setReturnForm({ ...returnForm, reason: e.target.value })}
                  data-testid="return-reason"
                  className="w-full border border-[#E7E5E4] rounded-md h-10 px-3 text-sm bg-white"
                >
                  <option value="">-- Pick a reason --</option>
                  <option value="Wrong size">Wrong size</option>
                  <option value="Different from what I ordered">Different from what I ordered</option>
                  <option value="Damaged / defective">Damaged / defective</option>
                  <option value="Quality not as expected">Quality not as expected</option>
                  <option value="No longer needed">No longer needed</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-[#78716C] block mb-1">Details (optional)</label>
                <Textarea
                  value={returnForm.reason_details}
                  onChange={(e) => setReturnForm({ ...returnForm, reason_details: e.target.value })}
                  placeholder="Anything else we should know?"
                  data-testid="return-details"
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button onClick={submitReturn} data-testid="return-submit" className="bg-[#7C1F30] hover:bg-[#8D2A3D] rounded-full">Submit request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
